import type { FaqItem, Testimonial, SocialPost } from '@/types'

export const heroProof = {
    count: '3,100+',
    label: 'quiet converts',
    avatars: [
        { src: '/images/proof-1.jpg', alt: 'Nuvéa customer' },
        { src: '/images/proof-2.jpg', alt: 'Nuvéa customer' },
        { src: '/images/proof-3.jpg', alt: 'Nuvéa customer' },
        { src: '/images/proof-4.jpg', alt: 'Nuvéa customer' },
        { src: '/images/proof-5.jpg', alt: 'Nuvéa customer' }
    ]
}

export const statementLines = [
    'Five actives that earn their place.',
    'One jar. One step.',
    'Skin that stops arguing.'
]

export const principles = [
    {
        number: '01',
        title: ['Five actives.', 'That is the list.'],
        body: 'Ceramides to rebuild, peptides to firm, niacinamide to settle redness, squalane to hold water, glycerin to keep it there. Nothing added to pad the label, nothing added to smell expensive.'
    },
    {
        number: '02',
        title: ['Measured on people,', 'not on promises.'],
        body: 'We ran a 12-week trial with 412 volunteers across six skin types before we sold a single jar. Barrier function, water loss and redness were measured at every visit. The numbers on this page are theirs.'
    },
    {
        number: '03',
        title: ['Concentration over', 'decoration.'],
        body: 'A bottle can carry thirty ingredients at doses too low to matter. We would rather carry five at doses that show up in a measurement, and spend the rest of the budget on how they are delivered.'
    },
    {
        number: '04',
        title: ['The jar you finish,', 'then reorder.'],
        body: 'Most skincare is abandoned half-used. Ours is designed to be emptied: one step, one texture, refillable, and priced so that repurchasing is not a decision you have to justify.'
    }
]

export const routineSteps = [
    {
        step: 'Step 01',
        title: 'Warm',
        body: 'Take a pea-sized amount and warm it between your fingertips for a few seconds. It turns from balm to fluid.',
        image: '/images/step-apply.png',
        alt: 'A woman holding the Nuvéa jar with a fresh swatch of cream on her cheek'
    },
    {
        step: 'Step 02',
        title: 'Sweep',
        body: 'Sweep upward and outward across the face and neck. No tugging, no massage routine, no second layer.',
        image: '/images/step-distribute.png',
        alt: 'A woman smiling as she blends the cream across her cheekbone'
    },
    {
        step: 'Step 03',
        title: 'Settle',
        body: 'Rest your palms against the skin for ten seconds. Warmth helps the lipids sink rather than sit.',
        image: '/images/step-press.png',
        alt: 'A woman pressing the formula into her skin, jar still in hand'
    }
]

export const activeIngredients = [
    { name: 'Ceramide NP', index: 'Active 01', role: 'Rebuilds the barrier' },
    { name: 'Peptide Complex', index: 'Active 02', role: 'Supports firmness' },
    { name: 'Niacinamide', index: 'Active 03', role: 'Settles redness' },
    { name: 'Squalane', index: 'Active 04', role: 'Holds water in' },
    { name: 'Glycerin', index: 'Active 05', role: 'Keeps it there' }
]

export const ingredientPillars = [
    {
        title: 'Five or fewer',
        number: '01',
        body: 'A hard ceiling, not a guideline. If something new goes in, something old has to earn its way out first.'
    },
    {
        title: 'Clinically measured',
        number: '02',
        body: 'Twelve weeks, 412 volunteers, six skin types. Independent assessors, and every figure published as recorded.'
    },
    {
        title: 'Dermatologist built',
        number: '03',
        body: 'Formulated by two practising dermatologists who were tired of undoing the damage done by other people’s routines.'
    }
]

export const skinBenefits = [
    'Stronger barrier',
    'Even tone',
    'Smoother texture',
    'Better bounce',
    'Water held overnight',
    'Less reactivity',
    'Clearer surface',
    'Natural light',
    'Calmer cheeks',
    'Tighter pores'
]

export const resultStats = [
    {
        value: 91.4,
        suffix: '%',
        decimals: 1,
        body: 'reported calmer, less reactive skin inside the first two weeks of switching.'
    },
    {
        value: 28,
        suffix: ' Days',
        decimals: 0,
        body: 'one full renewal cycle, which is when texture and tone changes became measurable.'
    }
]

export const testimonials: Testimonial[] = [
    {
        id: 'kai',
        quote: 'I fly most weeks and my skin used to land angrier than I did. The travel jar clears security and actually holds up, which is more than I can say for everything else I have carried.',
        name: 'Kai Tanaka',
        context: 'in the air four days a week',
        avatar: '/images/person-kai.png'
    },
    {
        id: 'diane',
        quote: 'I over-exfoliated for two years and wrecked my barrier doing it. This is the first thing that let my face calm down instead of asking more of it.',
        name: 'Diane Lawson',
        context: 'rebuilding after over-exfoliation',
        avatar: '/images/person-diane.png'
    },
    {
        id: 'jennifer',
        quote: 'One jar replacing nine steps sounded like marketing. Two months in, my colleagues keep asking what changed, and the honest answer is that I stopped doing so much.',
        name: 'Jennifer Thompson',
        context: 'down from a nine-step routine',
        avatar: '/images/person-jennifer.jpg'
    },
    {
        id: 'aisha',
        quote: 'I am 52 and I have tried the lot. This is the first jar I have ever actually finished, and the first refill I ordered before running out.',
        name: 'Aisha Rodriguez',
        context: 'six months in, third refill',
        avatar: '/images/person-aisha.png'
    },
    {
        id: 'sarah',
        quote: 'Mostly I was tired of guessing. One product, one texture, no decisions at eleven at night. The shelf is empty and my skin is better for it.',
        name: 'Sarah Mitchell',
        context: 'combination skin',
        avatar: '/images/person-sarah.png'
    },
    {
        id: 'michelle',
        quote: 'Everything broke me out, so I assumed this would too. It did not. The redness around my nose has gone for the first time since my twenties.',
        name: 'Michelle Kaplan',
        context: 'reactive, easily provoked skin',
        avatar: '/images/person-michelle.jpg'
    }
]

export const communityAvatars = [
    '/images/person-tara.png',
    '/images/person-jennifer.jpg',
    '/images/person-diane.png',
    '/images/person-kai.png',
    '/images/person-aisha.png'
]

export const homeFaqs: FaqItem[] = [
    {
        question: 'Will using less actually improve my skin?',
        answer: 'For most people, yes. Sensitivity and barrier damage are now widely understood to be over-treatment problems rather than under-treatment ones. Removing steps gives the barrier room to re-regulate. In our trial, 89% of participants saw measurable improvement within the first month of simplifying.'
    },
    {
        question: 'How long before I notice anything?',
        answer: 'Hydration and surface texture usually shift in the first 7 to 10 days. Tone, resilience and fine lines take longer, typically showing around day 28, which is roughly one full skin renewal cycle.'
    },
    {
        question: 'Is one formula really right for every skin type?',
        answer: 'Skin type is a less useful category than the industry suggests. Barrier function, water retention and inflammatory response work the same way in all of them. Nuvéa supports those, and what changes between people is how much you use and how often, not what you use.'
    },
    {
        question: 'What do I stop using?',
        answer: 'Start by cutting anything with fragrance, anything you are using twice a day out of habit, and any second moisturiser. Keep a gentle cleanser and sunscreen. Most people end up at three products and stay there.'
    },
    {
        question: 'How does the refill work?',
        answer: 'The glass vessel is permanent. When you reach the bottom, lift out the inner pod and click a new one in. It takes about five seconds and removes 94% of the packaging weight from every reorder.'
    },
    {
        question: 'Can I try it before committing to a full jar?',
        answer: 'The 20 ml travel size is two weeks of use and carries the same 30-day guarantee as the full size. If it is not for you, tell us and we will refund it, jar returned or not.'
    }
]

export const socialPosts: SocialPost[] = [
    { handle: '@elena.plainly', image: '/images/person-jennifer.jpg' },
    { handle: '@sarah.barefaced', image: '/images/person-michelle.jpg' },
    { handle: '@jess.oneshelf', image: '/images/person-diane.png' },
    { handle: '@priya.quiet', image: '/images/person-priya.png' },
    { handle: '@tara.lessis', image: '/images/person-tara.png' },
    { handle: '@mei.slowskin', image: '/images/person-kai.png' },
    { handle: '@leila.unfussed', image: '/images/person-aisha.png' },
    { handle: '@zoe.thirtydays', image: '/images/person-zoe.png' },
    { handle: '@cara.freckled', image: '/images/person-sarah.png' },
    { handle: '@aisha.refill', image: '/images/person-aisha-glow.png' },
    { handle: '@isabella.edited', image: '/images/person-isabella.png' },
    { handle: '@nia.nofilter', image: '/images/person-nia.png' }
]

export const socialHandle = '@nuvea.skin'
